/* Models and provider connections: the app's shapes, then the wire's.

   The wire is C2 of `tasks/byok-providers/00-PLAN.md` (`GET /api/v1/providers`
   and the connection routes), snake_case and verbatim. The app never reads it
   directly: `shared/api/models.ts::toModelsWorkspace` is the one mapper. */

export type ProviderId = "anthropic" | "openai" | "google" | "deepseek" | "moonshot" | "openrouter" | "venice" | "claude_code" | "codex" | "typesafe";
export const PROVIDERS: Record<ProviderId, string> = {
  anthropic: "Anthropic", openai: "OpenAI", google: "Google Gemini", deepseek: "DeepSeek", moonshot: "Kimi", openrouter: "OpenRouter", venice: "Venice AI",
  claude_code: "Claude Code (experimental)", codex: "Codex (experimental)", typesafe: "TypeSafe",
};
export const isSubscription = (provider: ProviderId): provider is "claude_code" | "codex" => provider === "claude_code" || provider === "codex";

/** A model and the key that runs it. `managed` is a model on no connection
    (`connection_id: null` on the wire): one of Definable's own models. */
export type ModelSelection = { modelId: string; source: "managed" } | { modelId: string; source: "connection"; connectionId: string };

/** USD per 1M tokens. A null cache rate: the provider doesn't bill cache tokens separately. */
export interface ModelPrice { input: number; output: number; cacheRead: number | null; cacheWrite: number | null }

export interface ModelOption {
  id: string;
  name: string;
  family: string;
  provider: ProviderId;
  /** Tools, Vision, PDF, Reasoning — the model's features in words. */
  capabilities: string[];
  maxOutputTokens: number;
  contextWindow: number;
  /** Null: the model takes no temperature. */
  temperature: { min: number; max: number } | null;
  /** The API list price; a subscription connection shows what its calls would cost on a key. */
  price: ModelPrice;
  deprecated: boolean;
  /** A decision model (Jev): answers a choice with probabilities and writes no text, so
      no agent runs on it. */
  decisionsOnly: boolean;
  /** Quick and cheap, or careful: only a fast model can be an agent's fast model. Null
      for a decision model. */
  tier: ModelTier | null;
  /** It can be told how hard to think (low, medium, high): the `effort` word. */
  takesEffort: boolean;
}
export type ModelTier = "fast" | "slow";
export type ConnectionStatus = "connected" | "invalid" | "restricted" | "unavailable";
export interface ProviderConnection {
  id: string;
  provider: ProviderId;
  name: string;
  /** The key's last four characters; the key itself never comes back. */
  suffix: string;
  status: ConnectionStatus;
  checkedAt: string;
  modelIds: string[];
  /** Models the key lists that nothing can run yet. */
  hiddenCount: number;
}
export interface ModelsWorkspace {
  /** Every model any live connection can run. */
  models: ModelOption[];
  /** Definable's own models: chosen with no connection, on Definable's accounts. */
  definable: ModelOption[];
  connections: ProviderConnection[];
  /** What a new agent starts on; null until an admin sets it. */
  defaultSelection: ModelSelection | null;
}
export interface ConnectionDraft { provider: ProviderId; name: string; key: string }

/* ------------------------------- the wire (C2) ------------------------------ */

export interface ApiProviderModel {
  provider: ProviderId;
  id: string;
  name: string;
  family: string;
  context: number;
  max_output: number;
  /** image, document, tools, thinking, json, system, stream, tool_stream, thinking_by_default, preserved_thinking, image_generation, decision */
  features: string[];
  temperature: boolean;
  price: { input: number; output: number; cache_read: number | null; cache_write: number | null };
  deprecated: boolean;
  /** From the model facts; null for a decision model. */
  tier: ModelTier | null;
}
export interface ApiConnection {
  id: string;
  provider: ProviderId;
  name: string;
  key_suffix: string;
  status: ConnectionStatus;
  checked_at: string;
  model_ids: string[];
  hidden_count: number;
}
export interface ApiModelChoice { model: string; connection_id: string | null }
/** `GET /providers`. */
export interface ApiProviders {
  providers: { id: ProviderId; name: string }[];
  models: ApiProviderModel[];
  /** Definable's own models, `provider` naming the maker (anthropic, openai). */
  definable: ApiProviderModel[];
  connections: ApiConnection[];
  default: ApiModelChoice | null;
}
/** The one element of a 3100004's `details`: who still uses the connection. */
export interface ConnectionInUse { agents: { id: string; name: string }[]; default: boolean }
